import React, {useEffect, useRef} from 'react';
import Dropzone from 'dropzone';
import {interpolate} from './api';

// Drag-and-drop / browse upload zone backed by the existing orphan-attachment upload endpoint
// (POST /deals/upload_deal_attachment). The server also enforces the 10 MB limit (FR-010).
export default function UploadZone({t, uploadUrl, acceptedExtensions, maxFileBytes, onUploaded, onError}) {
    const zoneRef = useRef(null);
    const callbacks = useRef({onUploaded, onError});
    callbacks.current = {onUploaded, onError};

    useEffect(() => {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        // Dropzone only treats clicks on the zone itself (or a .dz-message child) as clickable, so a click on
        // the inner text (e.g. the "browse" link) did nothing; register the children explicitly.
        const zone = zoneRef.current;
        const clickable = [zone, ...zone.querySelectorAll('*')];
        const dropzone = new Dropzone(zone, {
            clickable,
            url: uploadUrl,
            paramName: 'file',
            uploadMultiple: false,
            maxFilesize: maxFileBytes / (1024 * 1024),
            acceptedFiles: acceptedExtensions.join(','),
            previewsContainer: false,
            params: {authenticity_token: token},
            dictFileTooBig: t.file_too_big,
            dictInvalidFileType: t.invalid_file_type,
        });
        dropzone.on('success', (file, response) => {
            if (response && response.id && !response.err_msg) {
                callbacks.current.onUploaded({id: response.id, filename: file.name, size: file.size});
            } else {
                callbacks.current.onError(interpolate(t.upload_failed, {filename: file.name, error: response?.err_msg || ''}));
            }
        });
        dropzone.on('error', (file, message) => {
            const text = typeof message === 'string' ? message : (message?.err_msg || '');
            callbacks.current.onError(interpolate(t.upload_failed, {filename: file.name, error: text}));
        });
        return () => dropzone.destroy();
    }, [uploadUrl]);

    return (
        <div ref={zoneRef} className="di-upload" role="button" tabIndex={0}>
            <div className="di-upload__icon" aria-hidden="true">↑</div>
            <div className="di-upload__primary">
                {t.drop_text} <span className="di-upload__browse">{t.browse}</span>
            </div>
            <div className="di-upload__secondary">{t.file_types}</div>
        </div>
    );
}
